// Percorso file: src/auth/access.js
//
// MODULO DI ACCESSO UNICO — attuazione del "GATE UNICO" del contratto
// dati utente ↔ home (diario 5/8/2026, voce INTEGRAZIONE AL CONTRATTO).
//
// Regola: la persona possiede il ruolo X se `role === X` OPPURE ha un
// context ACTIVE con ruolo X. Una sola funzione, due sorgenti.
//
// Questo file è l'UNICA sede di:
//   - il vocabolario dei ruoli (ROLE_EQUIVALENTS, canonicalRole)
//   - il calcolo dei ruoli posseduti (getActiveRoles)
//   - la verifica di un permesso (canAccess)
//   - il ruolo indossato adesso (currentRole, isActingAs)
//   - la mappa ruolo → rotta (ROLE_ROUTES)
//   - la lettura del marcatore di completezza (isEnrolled)
// Nessun'altra parte dell'app deve ricostruire una di queste cose
// (audit 08 §0.6-0.7: oggi c'erano 11 gate diversi e 4 mappe).

/**
 * Mappa ruolo → rotta della sua home. UNICA in tutta l'app.
 * Le chiavi sono sempre MAIUSCOLE, come il campo `role` del contratto.
 * FAN ed ESPORT sono ruoli riservati, non offerti: chi li possiede non
 * ha una home e finisce sulla schermata di ruolo non riconosciuto.
 *
 * ⚠️ GUEST — il sesto, e NON è un socio (cantiere CLINICS, fetta A,
 * 12/9/2026; DISEGNO v2 §7). È il tutore autenticato del mondo clinic:
 * senza tessera di stagione, «solo entra», e nel mondo dei soci non
 * possiede nulla.
 *
 * Sta qui, e SOLTANTO qui, perché una home gli serve — deve atterrare da
 * qualche parte invece di fermarsi su «Role not recognised» — ma resta
 * FUORI da ogni whitelist di permesso: `RUOLI_REGISTRABILI`,
 * `RUOLI_AMMESSI_NEI_CONTEXTS`, `CONTEXT_AMMESSI_PER_RUOLO`, `AUTORITA`,
 * `RUOLI_CON_CHAT`. È il pattern FAN/ESPORT — un ruolo senza poteri è un
 * ruolo assente dagli elenchi, e ogni sede tratta l'assenza nel verso
 * sicuro (elenco vuoto, autorità 0, permesso negato) — con l'atterraggio
 * in più, che è la sola differenza. Il backend non cambia di una riga:
 * `requireRole` non tiene elenchi propri.
 *
 * ⚠️ NON deve MAI entrare in `ROLE_EQUIVALENTS`: lo tradurrebbe verso un
 * altro ruolo, e `canAccess` canonicalizza anche il ruolo RICHIESTO.
 *
 * ⚠️ Un documento GUEST NON nasce da `buildUserDocument` (backend): quel
 * costruttore lancia sui ruoli non registrabili, scrive `status: PENDING`
 * e fabbrica una tessera con `cardSeason` — cioè proprio ciò che al GUEST
 * è vietato. Il GUEST ha una porta di creazione propria sul backend (la
 * porta pubblica delle clinic); questa sede lo fa soltanto RICONOSCERE.
 */
export const ROLE_ROUTES = {
  SUPERADMIN: "/home/superadmin",
  ADMIN: "/home/admin",
  COACH: "/home/coach",
  PLAYER: "/home/player",
  PARENT: "/home/parent",
  GUEST: "/home/guest",
};

/**
 * Etichette leggibili dei ruoli. Non è una mappa d'accesso: serve solo a
 * scrivere i ruoli a schermo senza il tutto-maiuscolo, vietato fuori
 * dalle pastiglie (doc 07 §3.2).
 */
export const ROLE_LABELS = {
  SUPERADMIN: "Super Admin",
  ADMIN: "Admin",
  COACH: "Coach",
  PLAYER: "Player",
  PARENT: "Parent",
  // Il tutore del mondo clinic (v. il cappello di ROLE_ROUTES): qui c'è
  // solo la sua resa a schermo, che non concede nulla. Le tendine che
  // filtrano per ruolo NON nascono da questa tabella — sono copie a mano
  // (UsersList) o nascono dai dati (UniformDistribution) — quindi questa
  // riga non aggiunge una voce da nessuna parte.
  GUEST: "Guest",
};

/**
 * Normalizzazione unica di ogni valore che arrivi dal documento utente:
 * sola GRAFIA (spazi e maiuscole), nessuna traduzione.
 * Si usa anche su campi che non sono ruoli (status, team, categorie):
 * per questo qui dentro non entra mai il vocabolario — vedi canonicalRole.
 */
export const normalizeRole = (value) =>
  String(value ?? "").trim().toUpperCase();

/**
 * VOCABOLARIO DEI RUOLI — equivalenze fra il modello storico e quello
 * nuovo (diario 8/8/2026, apertura U1-F, decisione 2).
 *
 * ⚠️ SOLA LETTURA. È un traduttore per i documenti storici, NON una
 * resurrezione dei ruoli morti: MANAGER, TD, MANAGERTD e CHILD non
 * torneranno in alcun menù, tendina o controllo, e nessuno dei ruoli veri
 * (ROLE_ROUTES) si scrive passando da qui.
 * Al futuro: da P5 nessuno di quei ruoli è più offerto dai punti CENSITI in
 * questo commento. La voce
 * «Manager» di `UserContextManager` è caduta in entrambi i suoi punti — le
 * caselle che assegnano un context (`CONTEXT_TYPES`, ridotta ai soli
 * PLAYER/COACH/PARENT che il backend ammette, server.js, con PARENT
 * aggiunto perché mancava) e la tendina di filtro dell'elenco, dove al suo
 * posto è entrato «Parent». Quella pagina non offre più nemmeno ADMIN e
 * SUPERADMIN fra le caselle che ASSEGNANO un context: da P5 il ruolo di un
 * amministratore si assegna in una sede sola, `PUT /users/:id`. («Admin»
 * resta invece fra le voci della tendina che FILTRA l'elenco, dove non
 * assegna nulla.)
 *
 * Dove non c'era già più: la select di `EditMemberModal` che ASSEGNA il
 * `role` offre i soli ruoli del modello nuovo — «Manager», «Fan/Supporter» e
 * «eSport Member» sono cadute in P4, con la decisione 9 dell'apertura U1-F;
 * la tendina di filtro di UsersList era caduta in P3, insieme a tutti i
 * confronti letterali di ruolo di quel file. A quella stessa select, subito
 * dopo P5 e mai prima (commit P5-bis, eredità D8 di P4), si aggiunge la voce
 * «Admin» riservata al SUPERADMIN: è la nuova sede, e si apre solo quando la
 * vecchia è chiusa, perché il backend vuole «una porta sola per dato»
 * (sede: il ramo `if (campo === "role")` di `PUT /users/:id` in
 * `backend/server.js`, sotto il commento «UNA PORTA SOLA PER DATO» — si
 * cita per contenuto e non per riga, che slitta a ogni fetta).
 * Restano i confronti degli altri file censiti, nei
 * rispettivi punti.
 *
 * ⚠️ Un residuo NON censito è emerso in P5, e non va taciuto perché oggi non
 * si vede: `PermissionsManager` costruisce tendina e matrice dalle chiavi di
 * `data/permissions.json`, che sono ancora quelle del modello storico
 * (Manager, eSport Member, Fan/Supporter). A schermo non compaiono più perché
 * la pagina non ha più alcuna sorgente: `backend/routes/permissions.js` è
 * stato CANCELLATO (cantiere SICUREZZA, gruppo 2), non solo lasciato non
 * montato, e la pagina resta a «Loading…». Le chiavi storiche però
 * sopravvivono in `data/permissions.json`: il residuo è nel dato, non più nel
 * meccanismo. Da assegnare a un punto proprio.
 *
 * ⚠️ UNA DEROGA ESISTE, ed è dichiarata: `UserContextManager.openEditor` usa
 * `canonicalRole` in una lettura che finisce in scrittura (il Save riscrive i
 * contexts per intero). È ammessa perché la conversione la impone il BACKEND
 * — che nei contexts rifiuta la grafia storica con un 403 — la decide un
 * amministratore premendo Save, e la pagina la dichiara a schermo prima che
 * accada. Le tre condizioni valgono lì e solo lì: non è un precedente.
 *
 * ⚠️ DIVIETO D'USO IN SCRITTURA. Questa tabella non deve MAI decidere
 * cosa finisce nei dati: nessuna chiamata a canonicalRole può stare a
 * monte di un salvataggio, di un payload verso il backend o di un valore
 * scelto in un modulo. Ciò che si SCRIVE resta governato dalla grafia
 * originale del documento (decisione 8 della chiusura U1-S: confronti
 * tolleranti, dati invariati; e backend server.js, che scrive di
 * proposito la grafia storica). La conversione dei dati è materia del
 * travaso post-27/9, non di questo file.
 *
 * Serve finché in Firestore convivono le due grafie. Il giorno in cui i
 * dati saranno convertiti, questa tabella si svuota e nient'altro cambia.
 *
 * ⚠️ IL BACKEND TRADUCE ANCHE LUI, ORA — e questa voce diceva il
 * contrario. Resta vero che `normalizzaRuolo` (server.js) fa la sola
 * grafia, ma accanto gli è cresciuto `ruoloCanonico`, che è lo STESSO
 * vocabolario di qui (MANAGER/TD/MANAGERTD → COACH, CHILD → PLAYER):
 * due sedi, un elenco solo. Da F4 lo usano le squadre del coach, da F8
 * i gate di `GET /users/:id` e `GET /users/:id/photo`.
 *
 * ⚠️ CADE QUINDI IL «PREZZO DELLO STATO INTERMEDIO» che questa voce
 * annunciava per il collaudo di P1: un documento storico "Manager" non
 * prende più 403 da `GET /users/:id` e non si ritrova più con la pagina
 * aperta e i dati vuoti — il ramo coach del backend gli si apre, e le
 * sue squadre le calcola per intero. Al collaudo, con un utente finto in
 * grafia storica, ci si aspetta ora una pagina PIENA.
 *
 * ⚠️ RESTA UN SOLO LATO NON TRADOTTO, ed è la CHAT: il gate della chat
 * di squadra in `POST /messages` confronta ancora i nomi con un
 * normalizzatore proprio (`normalizeTeam`), diverso da quello dei
 * permessi. È dichiarato nel commento di `partecipaAllaSquadra` in
 * server.js e appartiene al cantiere della chat, non a questo file.
 */
export const ROLE_EQUIVALENTS = {
  MANAGER: "COACH",
  TD: "COACH",
  MANAGERTD: "COACH",
  CHILD: "PLAYER",
};

/**
 * Il ruolo in forma canonica: grafia normalizzata + vocabolario.
 * È la funzione con cui si CONFRONTANO i ruoli; `normalizeRole` resta per
 * tutto il resto (status, team, categorie), dove una traduzione sarebbe
 * un danno — una squadra chiamata "TD" non è un allenatore.
 *
 * @param {*} value valore grezzo (documento utente o context)
 * @returns {string} ruolo canonico MAIUSCOLO ("" se vuoto)
 */
export function canonicalRole(value) {
  const role = normalizeRole(value);
  return ROLE_EQUIVALENTS[role] || role;
}

/**
 * L'insieme dei ruoli posseduti da una persona.
 * = `role` di primo livello + i `role` dei contexts con status ACTIVE,
 * in forma CANONICA, senza doppioni, nell'ordine in cui si incontrano.
 *
 * Con il vocabolario, un documento storico `role: "Manager"` risulta
 * COACH e le due grafie della stessa persona collassano in un ruolo solo
 * (prima ne contava due, e la barra offriva uno Switch che portava a una
 * scelta sola: HomeContext mostra solo i ruoli con una home, quindi
 * MANAGER compariva lì sotto come «Not available yet»).
 *
 * ⚠️ Lo status del context si legge con `normalizeRole`: l'`add` qui
 * sotto normalizzava il RUOLO e il filtro sui contexts lasciava lo STATUS
 * crudo, così un context salvato `status: "Active"` portava un ruolo che
 * il gate non vedeva. Il backend applica già questo metro
 * (server.js). Nessun default: status assente ≠ ACTIVE, come sul
 * backend.
 *
 * ⚠️ La difesa vale per TUTTI i ruoli, ADMIN e SUPERADMIN compresi: un
 * context `{role: "ADMIN", status: "Active"}` prima non contava e ora sì.
 * Non è un permesso nuovo — il backend lo concedeva già (server.js),
 * quindi qui il frontend smette di essere più severo di chi decide
 * davvero — ma è l'unico punto di questo file che tocca ruoli
 * privilegiati: va provato al collaudo, con dati finti nelle due grafie.
 *
 * @param {object|null} user utente di AuthContext
 * @returns {string[]} array di ruoli canonici (vuoto se utente nullo)
 */
export function getActiveRoles(user) {
  if (!user) return [];

  const roles = [];

  const add = (rawRole) => {
    const role = canonicalRole(rawRole);
    if (role && !roles.includes(role)) roles.push(role);
  };

  add(user.role);

  if (Array.isArray(user.contexts)) {
    user.contexts.forEach((ctx) => {
      if (ctx && normalizeRole(ctx.status) === "ACTIVE") add(ctx.role);
    });
  }

  return roles;
}

/**
 * Vero se la persona POSSIEDE il ruolo richiesto (permesso).
 * Canonico sui due lati del confronto, come ogni confronto di ruolo.
 *
 * ⚠️ La canonicalizzazione vale anche sul ruolo RICHIESTO: senza,
 * chiedere un ruolo storico sarebbe sempre falso e nascerebbe un secondo
 * metro. Il rovescio è che chiedere `"TD"` equivale a chiedere `"COACH"`:
 * le pagine devono continuare a chiedere solo i ruoli veri
 * (ROLE_ROUTES), mai un ruolo morto.
 *
 * @param {object|null} user utente di AuthContext
 * @param {string} role ruolo richiesto (MAIUSCOLO, uno dei cinque veri)
 */
export function canAccess(user, role) {
  const wanted = canonicalRole(role);
  if (!wanted) return false;
  return getActiveRoles(user).includes(wanted);
}

// ---------------------------------------------------------------------
// L'AUTORITÀ SUL BERSAGLIO — «posso agire su questa persona?»
// (regola del proprietario, 6/8/2026; sede del server: server.js)
//
// `canAccess` risponde a «che cosa POSSO FARE»; questa scala risponde a «SU
// CHI». Sono due domande diverse e servono insieme: un ADMIN ha il permesso
// di cancellare, ma non su un altro ADMIN.
//
// Regola unica, tre righe che sono la stessa:
//   - nessuno agisce su un bersaglio SUPERADMIN
//   - solo un SUPERADMIN agisce su un bersaglio ADMIN
//   - in generale: mai su un bersaglio di autorità PARI O SUPERIORE
// Basta confrontare i livelli e pretendere che quello del chiamante sia
// STRETTAMENTE maggiore. «Pari» è escluso di proposito — fra pari grado non
// si agisce, così due ADMIN non possono disfarsi a vicenda, e nemmeno da
// soli sul proprio documento.
//
// ⚠️ QUI NON SI PROTEGGE NULLA, ed è il punto. L'autorità la applica il
// backend, che rifiuta con 403 (`puoAgireSuBersaglio`, server.js).
// Questa copia serve alle VISTE: non offrire una porta murata, cioè non
// disegnare un comando che il server rifiuterebbe comunque. Se le due
// divergono, comanda il backend.
//
// ⚠️ REPLICA FEDELE, NON REINTERPRETAZIONE. I numeri sono quelli del server,
// uno per uno. Le grafie storiche MANAGER / TD / MANAGERTD stanno nella
// tabella del server perché là i ruoli si normalizzano soltanto (grafia); qui
// `getActiveRoles` applica anche il VOCABOLARIO e le porta già a COACH. Le si
// tiene lo stesso, e valgono lo stesso numero: se un domani questa tabella
// venisse letta con ruoli grezzi, il risultato non cambierebbe.
// ---------------------------------------------------------------------

const AUTORITA = {
  SUPERADMIN: 3,
  ADMIN: 2,
  COACH: 1,
  MANAGER: 1,
  TD: 1,
  MANAGERTD: 1,
};

/**
 * Il livello d'autorità di una persona: il più alto fra i ruoli POSSEDUTI
 * (`role` di primo livello + contexts ACTIVE, come ogni altra domanda di
 * ruolo). Chi non ha nessuno dei ruoli in tabella vale 0.
 *
 * @param {object|null} user documento utente (chi guarda o il bersaglio)
 * @returns {number} 0 | 1 | 2 | 3
 */
export function livelloAutorita(user) {
  let livello = 0;

  for (const ruolo of getActiveRoles(user)) {
    // `hasOwnProperty` e non `AUTORITA[ruolo] || 0`: con l'accesso diretto un
    // ruolo che si chiamasse "constructor" leggerebbe dal prototipo. È la
    // stessa cautela del server.
    const valore = Object.prototype.hasOwnProperty.call(AUTORITA, ruolo)
      ? AUTORITA[ruolo]
      : 0;
    if (valore > livello) livello = valore;
  }

  return livello;
}

/**
 * Vero se `chiamante` ha autorità STRETTAMENTE maggiore di `bersaglio`, cioè
 * se il server accetterebbe di farlo agire su quella persona.
 *
 * Ingloba il caso «sé stessi»: stessa persona = stessi ruoli = livelli pari,
 * e «pari» non basta. Non serve quindi affiancargli un confronto d'identità,
 * che sarebbe un secondo metro sulla stessa domanda.
 *
 * @param {object|null} chiamante utente collegato
 * @param {object|null} bersaglio documento della persona su cui si agisce
 * @returns {boolean}
 */
export function puoAgireSuBersaglio(chiamante, bersaglio) {
  return livelloAutorita(chiamante) > livelloAutorita(bersaglio);
}

// ---------------------------------------------------------------------
// IL MARCATORE DI COMPLETEZZA — `enrolled` (RL-3, letto da RL-6)
//
// `enrolled` risponde a UNA domanda sola: la persona ha consegnato tutti i
// dati che il club le chiede per la sua figura? Non è un permesso e non è
// uno stato di approvazione — quello è `status` (backend server.js).
//
// ⚠️ L'AUTORITÀ È IL BACKEND. Il campo lo CALCOLA il server (calcolaEnrolled,
// e per i genitori enrolledConFamiglia, con la cascata sui figli) e lo scrive
// alla nascita del documento e a ogni scrittura successiva. Il frontend lo
// LEGGE dal documento utente e basta: qui non si ricalcola nulla, non si
// deduce nulla dai campi anagrafici, non si scrive nulla.
//
// ⚠️ CONVENZIONE «CAMPO ASSENTE = COMPLETO», ed è responsabilità dei LETTORI
// (backend server.js). I documenti nati prima del modello v2 — oggi
// tutta l'anagrafica — NON hanno il campo, e il backend non glielo introduce
// mai (regola transitoria, server.js). Trattarli da incompleti
// chiuderebbe fuori dall'app ogni socio in regola da anni.
// Perciò la sola cosa che spegne il marcatore è un `false` ESPLICITO: campo
// assente, `undefined` o qualunque altro valore = completo. Nel dubbio si
// apre, non si chiude — è il verso opposto a quello dei ruoli (canAccess),
// e la ragione è che qui non si concede un permesso, si chiede di compilare
// un modulo.
// ---------------------------------------------------------------------

/**
 * Vero se la scheda della persona è COMPLETA secondo il backend.
 *
 * @param {object|null} user utente di AuthContext
 * @returns {boolean} falso solo se il documento porta `enrolled: false`
 */
export function isEnrolled(user) {
  // Senza utente non c'è nulla da completare. Il caso è già escluso a monte
  // dal cancello (RequireRole manda al login prima di arrivare qui): il
  // valore serve solo a non far dipendere questa funzione da chi la chiama.
  if (!user) return true;

  return user.enrolled !== false;
}

// ---------------------------------------------------------------------
// LA CONFERMA ANNUALE — `deveCompletareStagione` (RENEW-COMPLETAMENTO)
//
// Seconda domanda, accanto a quella di `isEnrolled`, e non la stessa:
//   `enrolled` → «i dati anagrafici ci sono?»
//   questa     → «per l'anno di enrollment hai confermato?»
// Un rinnovante ha i dati dell'anno prima, quindi `enrolled` acceso, e va
// fermato lo stesso finché non rivede la scheda e riaccetta i termini. È un
// atto ANNUALE, non la completezza dei dati.
//
// ⚠️ IL CALCOLO NON È QUI, ed è una scelta, non una dimenticanza. Il campo lo
// prepara AuthContext dalle tessere GREZZE
// (`completamentoStagionaleDaFare`), perché la tessera dell'anno di
// enrollment, in fase di sovrapposizione, non sopravvive al cancello della
// stagione e a valle non esiste più: una domanda posta qui sulle tessere
// risponderebbe «no» a chiunque, cioè un gate inerte. Questa funzione LEGGE
// il booleano e basta.
//
// ⚠️ E DA RENEW-COMPLETAMENTO-FAMIGLIA IL BOOLEANO HA DUE TERMINI, non uno:
// le tessere della persona E la risposta di FAMIGLIA che il backend scrive
// sul documento del genitore (`seasonConfirmedFamily`), perché un genitore
// che ha confermato per sé non è a posto finché non hanno confermato anche i
// suoi figli. Il secondo termine qui non si potrebbe nemmeno calcolare: i
// figli, dal frontend, non si vedono. È una ragione in più perché il calcolo
// non stia qui — e perché questa funzione continui a leggere e basta.
//
// ⚠️ VERSO OPPOSTO a quello di `isEnrolled`: là la sola cosa che chiude è un
// `false` esplicito, qui la sola cosa che manda al completamento è un `true`
// esplicito. Campo assente = niente da confermare. Le due convenzioni si
// somigliano perché dicono la stessa cosa: nel dubbio non si chiude fuori
// nessuno.
// ---------------------------------------------------------------------

/**
 * Vero se la persona deve ancora confermare il completamento per l'anno di
 * enrollment (RENEW-COMPLETAMENTO).
 *
 * @param {object|null} user utente di AuthContext
 * @returns {boolean} vero solo se il campo derivato vale `true`
 */
export function deveCompletareStagione(user) {
  // Senza utente non c'è nulla da confermare. Il caso è già escluso a monte
  // dal cancello, come per `isEnrolled`.
  if (!user) return false;

  return user.deveCompletareStagione === true;
}

// ---------------------------------------------------------------------
// IL CAPPELLO INDOSSATO ADESSO — due domande diverse, due funzioni
// (diario 8/8/2026, apertura U1-F, decisione 1).
//
//   COMANDI (modifica, cancellazione, export) → si danno per PERMESSO:
//   `canAccess` / `hasRole`, cioè i ruoli POSSEDUTI, contexts compresi.
//
//   VISTE PERSONALI e filtri («la mia settimana», «le mie squadre») →
//   seguono il cappello indossato ADESSO: `currentRole` / `isActingAs`.
//
// Confonderle è il difetto storico dell'app: un allenatore che ha anche
// un context da giocatore non deve perdere i comandi da allenatore
// (permesso) né vedersi la settimana di tutto il club (vista).
// ---------------------------------------------------------------------

/**
 * Il ruolo che la persona sta indossando in questo momento.
 * = il ruolo dell'`activeContext` di AuthContext; in sua assenza il
 * `role` di primo livello. In forma canonica.
 *
 * Il context attivo vale solo se ACTIVE e con un ruolo valorizzato: un
 * context spento o senza ruolo non è un cappello, e si ricade sul ruolo
 * base invece di restare senza identità.
 *
 * ⚠️ COSA È OGGI l'`activeContext`, per non costruirci sopra un equivoco:
 * ha DUE scrittori, non più uno. AuthContext lo imposta all'ingresso col
 * PRIMO context ACTIVE (`setActiveContext(contexts[0] || null)`), e da F6
 * lo Switch di HomeContext lo IMPOSTA a sua volta: `chooseRole` cerca fra
 * i contexts la tessera ATTIVA del ruolo scelto e la indossa, e quando
 * quella tessera non esiste — è il caso del `role` di primo livello —
 * indossa un cappello SINTETICO (stesso ruolo, status ACTIVE, squadre
 * vuote nelle due forme) che vive nello stato della sessione e non si
 * salva da nessuna parte. Poi naviga.
 *
 * ⚠️ IL LIMITE DI P1 È CHIUSO (F6, 24/8/2026). Fino ad allora lo Switch
 * navigava soltanto (`navigate(ROLE_ROUTES[role])`) e il cappello restava
 * al primo context ACTIVE: chi aveva più context vedeva «la mia settimana»
 * rispondere a quel context invece che al ruolo scelto. Questo commento
 * dichiarava quel limite come ATTIVO — non lo è più, e le cinque viste
 * personali del calendario che questa funzione governa obbediscono ora
 * alla scelta della persona. Restano vere le due righe di sempre: il
 * cappello viene comunque da un ruolo POSSEDUTO e attivo, e chi ha il solo
 * `role` di primo livello non ha cappelli da scegliere.
 *
 * @param {object|null} user utente di AuthContext
 * @param {object|null} activeContext context attivo (AuthContext)
 * @returns {string} ruolo canonico ("" se non determinabile)
 */
export function currentRole(user, activeContext = null) {
  if (activeContext && normalizeRole(activeContext.status) === "ACTIVE") {
    const contextRole = canonicalRole(activeContext.role);
    if (contextRole) return contextRole;
  }

  return canonicalRole(user?.role);
}

/**
 * Vero se il cappello indossato adesso è uno di quelli indicati.
 * Da usare per le viste personali e i filtri, MAI per i comandi.
 *
 * @param {object|null} user utente di AuthContext
 * @param {object|null} activeContext context attivo (AuthContext)
 * @param {...string} roles ruoli accettati
 */
export function isActingAs(user, activeContext, ...roles) {
  const attuale = currentRole(user, activeContext);
  if (!attuale) return false;

  return roles.some((role) => canonicalRole(role) === attuale);
}

/**
 * Etichetta leggibile di un ruolo; se sconosciuto, il ruolo così com'è.
 *
 * ⚠️ NON applica il vocabolario, di proposito: è resa a schermo, non un
 * confronto, e tradurre qui cambierebbe ciò che si legge nella colonna
 * Role di TeamAssignment. Deciso l'8/8/2026 in apertura di P0: la
 * questione (oggi `roleLabel("Manager")` scrive "MANAGER" tutto
 * maiuscolo, che il doc 07 §3.2 vieta fuori dalle pastiglie) resta
 * a verbale per P4/U1-G.
 */
export function roleLabel(role) {
  const normalized = normalizeRole(role);
  return ROLE_LABELS[normalized] || normalized;
}
